/**
 * Pure helpers naming the tonality of a piece.
 *
 * They served the harmonic intro screen, which is gone; what is left is the
 * tonality the S0 chord presentation needs to give its degrees a meaning.
 * The file keeps its name because that view imports it.
 *
 * Lives in `components/learn-piece/` (UI-side) rather than in the journey
 * lib because it depends on the runtime `Song` shape, same rule as
 * `phrase-chords.ts`. Keeping it pure means the Jest layer can cover the
 * tonality derivation without mounting any React tree.
 */

import type { AnalysisKey } from "@/lib/music/analyzer-types";
import type { DetectedKey } from "@/lib/music/key";
import type { NoteNotation } from "@/lib/music/notation";
import {
  tonalityFromDetectedKey,
  tonalityLabel,
  type Tonality,
} from "@/lib/music/tonality";

// ── Public types ──────────────────────────────────────────────────

/**
 * Minimal Song surface consumed by `extractIntroHarmonyInfo`. Aliased
 * structurally so unit tests can hand-build a fixture without depending
 * on the heavy `Song` class.
 */
export type IntroSongLike = {
  getAnalysis: () => { key: AnalysisKey } | null;
  getKey: () => DetectedKey | null;
};

/** The harmonic facts about a piece the learning views read. */
export type IntroHarmonyInfo = {
  /** Strongly-typed tonality, suitable for `HarmonyTonality`. `null` when none was detected. */
  readonly tonality: Tonality | null;
};

// ── Public API ────────────────────────────────────────────────────

/**
 * Reads the harmonic facts off a loaded Song.
 *
 * Tonality resolution, two sources of truth coexist:
 *
 *   1. `getAnalysis().key` (preferred): the analyzer key, richer
 *      (carries a stable `label` like `"C major"`).
 *   2. `getKey()`: the local Krumhansl-Schmuckler detection used as
 *      a fallback for songs imported without an analyzer payload.
 *
 * If neither yields a key, `tonality` is `null` and the view should
 * degrade gracefully.
 */
export function extractIntroHarmonyInfo(song: IntroSongLike): IntroHarmonyInfo {
  return { tonality: resolveTonality(song) };
}

/**
 * Human-readable tonality label, e.g. `"Do majeur"` (proxies
 * `tonalityLabel`). Centralised so the view layer doesn't reach into
 * `lib/music/tonality.ts` directly.
 */
export function formatTonalityLabel(
  tonality: Tonality,
  notation: NoteNotation,
): string {
  return tonalityLabel(tonality, notation);
}

// ── Internals ─────────────────────────────────────────────────────

function resolveTonality(song: IntroSongLike): Tonality | null {
  const analysis = song.getAnalysis();

  if (analysis !== null) {
    const k = analysis.key;

    return { tonic: k.tonic, mode: k.mode };
  }

  return tonalityFromDetectedKey(song.getKey());
}
